import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePageContent } from "@/i18n/pageContent";

const COUNTRIES = {
  it: [
    { name: "Italy", text: "Custodisce l'eredità dell'arte classica e del Rinascimento: città-museo, musica, teatro." },
    { name: "France", text: "Lunga tradizione letteraria, pittorica e cinematografica, unita a una forte sensibilità estetica." },
    { name: "Germany", text: "Terra di filosofia, musica classica e design contemporaneo. Dialogo continuo tra eredità e sperimentazione." },
    { name: "Spain", text: "Vibra di colore, danza e celebrazioni popolari: dal flamenco all'architettura modernista." },
    { name: "Portugal", text: "Racconta l'anima dell'oceano attraverso poesia, azulejos e fado." },
    { name: "Belgium", text: "Crocevia culturale: fumetto d'autore, arte fiamminga, scena contemporanea vibrante." },
    { name: "Netherlands", text: "Pittura dall'età dell'oro all'arte concettuale e architettura innovativa." },
    { name: "Luxembourg", text: "Scena culturale raffinata e internazionale, plurilingue." },
    { name: "Ireland", text: "Musica, letteratura e miti antichi: un immaginario potente e poetico." },
    { name: "Denmark", text: "Etica del design essenziale e forte tradizione narrativa, dalle fiabe alle serie moderne." },
    { name: "Sweden", text: "Contributi importanti a musica, cinema e design funzionale." },
    { name: "Finland", text: "Creatività, natura e architettura: forte attenzione all'educazione artistica." },
    { name: "Austria", text: "Sotto il segno della musica classica e dell'eleganza mitteleuropea." },
    { name: "Greece", text: "Custodisce le radici dell'arte occidentale: scultura antica, teatro, paesaggi vivi." },
    { name: "Cyprus", text: "Incontro tra tradizioni europee e mediterranee: arte, artigianato, archeologia." },
    { name: "Malta", text: "Secoli di incontri culturali nei templi megalitici, città fortificate, arti visive." },
    { name: "Slovenia", text: "Tradizioni alpine e influenze adriatiche in equilibrio armonico." },
    { name: "Croatia", text: "Patrimonio storico, città costiere e forte identità musicale." },
    { name: "Poland", text: "Ricca tradizione letteraria, musicale e teatrale, modellata da una profonda consapevolezza storica." },
    { name: "Czech Republic", text: "Architettura, cinema, cultura urbana vibrante." },
    { name: "Slovakia", text: "Folklore, musica tradizionale, arti applicate." },
    { name: "Hungary", text: "Forte identità artistica: musica, danza e arti visive." },
    { name: "Romania", text: "Patrimonio diverso: dal folklore alle nuove espressioni creative." },
    { name: "Bulgaria", text: "Canti, danze e rituali antichi riconosciuti come patrimonio culturale europeo." },
    { name: "Estonia", text: "Innovazione digitale applicata alla cultura: arte contemporanea, multimediale." },
    { name: "Latvia", text: "Tradizione corale celebrata e grandi festival musicali." },
    { name: "Lithuania", text: "Arte popolare, simbolismo e una creatività contemporanea dinamica." },
  ],
  en: [
    { name: "Italy", text: "Custodian of the heritage of classical and Renaissance art: museum-cities, music, theatre." },
    { name: "France", text: "A long literary, pictorial and cinematographic tradition, combined with strong aesthetic sensitivity." },
    { name: "Germany", text: "Land of philosophy, classical music and contemporary design. A continuous dialogue between heritage and experimentation." },
    { name: "Spain", text: "Vibrates with colour, dance and popular celebrations: from flamenco to modernist architecture." },
    { name: "Portugal", text: "Tells the soul of the ocean through poetry, azulejos and fado." },
    { name: "Belgium", text: "Cultural crossroads: auteur comics, Flemish art, vibrant contemporary scene." },
    { name: "Netherlands", text: "Painting from the Golden Age to conceptual art and innovative architecture." },
    { name: "Luxembourg", text: "Refined and international cultural scene, multilingual." },
    { name: "Ireland", text: "Music, literature and ancient myths: a powerful and poetic imagery." },
    { name: "Denmark", text: "Ethics of essential design and a strong narrative tradition, from fairy tales to modern series." },
    { name: "Sweden", text: "Important contributions to music, cinema and functional design." },
    { name: "Finland", text: "Creativity, nature and architecture: a strong focus on artistic education." },
    { name: "Austria", text: "Under the sign of classical music and Mitteleuropean elegance." },
    { name: "Greece", text: "Guardian of the roots of Western art: ancient sculpture, theatre, living landscapes." },
    { name: "Cyprus", text: "Meeting point of European and Mediterranean traditions: art, craftsmanship, archaeology." },
    { name: "Malta", text: "Centuries of cultural encounters in megalithic temples, fortified cities, visual arts." },
    { name: "Slovenia", text: "Alpine traditions and Adriatic influences in harmonious balance." },
    { name: "Croatia", text: "Historical heritage, coastal cities and strong musical identity." },
    { name: "Poland", text: "Rich literary, musical and theatrical tradition, shaped by deep historical awareness." },
    { name: "Czech Republic", text: "Architecture, cinema, vibrant urban culture." },
    { name: "Slovakia", text: "Folklore, traditional music, applied arts." },
    { name: "Hungary", text: "Strong artistic identity: music, dance and visual arts." },
    { name: "Romania", text: "Diverse heritage: from folklore to new creative expressions." },
    { name: "Bulgaria", text: "Ancient songs, dances and rituals recognised as European cultural heritage." },
    { name: "Estonia", text: "Digital innovation applied to culture: contemporary, multimedia art." },
    { name: "Latvia", text: "Celebrated choral tradition and major music festivals." },
    { name: "Lithuania", text: "Folk art, symbolism and dynamic contemporary creativity." },
  ],
};

const CLOSING = {
  it: { intro: "Ciò che unisce questa straordinaria diversità è il ", strong: "dialogo culturale", post: ": il desiderio di conoscersi, condividere storie, creare insieme uno spazio comune di immaginazione e creatività, in cui le differenze non si cancellano, ma si incontrano. Una pluralità di voci che, insieme, formano un'unica grande narrazione culturale." },
  en: { intro: "What unites this extraordinary diversity is ", strong: "cultural dialogue", post: ": the desire to get to know one another, share stories, and create together a common space of imagination and creativity, where differences are not erased but meet. A plurality of voices that together form a single great cultural narrative." },
};

export default function EUHeritage() {
  const { i18n } = useTranslation();
  const c = usePageContent("euHeritage");
  const lang = i18n.language === "en" ? "en" : "it";
  const list = COUNTRIES[lang];
  const closing = CLOSING[lang];

  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        title={<>{c.titleA} <span className="italic">{c.titleB}</span>.</>}
        subtitle={c.subtitle}
        image="/eu-heritage-hero.png"
        imageFit="contain"
      />
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
            {list.map((country, i) => (
              <article key={country.name} className="border-t border-[#e2e8f0] pt-6 reveal">
                <div className="flex items-baseline gap-3">
                  <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#d4af37] font-body">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-2xl md:text-[28px] text-[#1a365d] leading-snug">{country.name}</h3>
                </div>
                <p className="font-body text-[#475569] text-[14.5px] leading-[1.8] mt-3">{country.text}</p>
              </article>
            ))}
          </div>
          <div className="mt-16 md:mt-24 border-t border-[#e2e8f0] pt-12 max-w-3xl reveal">
            <p className="font-body text-[#1e293b] text-base md:text-lg leading-[1.85]">
              {closing.intro}<strong className="text-[#1a365d]">{closing.strong}</strong>{closing.post}
            </p>
            <Link to="/eu-cee" className="btn-outline mt-10">
              EU – CEE <ArrowRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
